import { ChevronDown, Search } from "lucide-react";
import { ReactNode, useEffect, useRef, useState } from "react";

interface FilterOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

interface FilterDropdownProps {
  label: string;
  icon?: ReactNode;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  className?: string;
  /** Show a search input above the options. Defaults to on for long lists. */
  searchable?: boolean;
}

export function FilterDropdown({
  label,
  icon,
  value,
  options,
  onChange,
  className = "",
  searchable,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label || label;

  const showSearch = searchable ?? options.length > 8;
  const trimmedQuery = query.trim().toLowerCase();
  const visibleOptions = trimmedQuery
    ? options.filter((opt) => opt.label.toLowerCase().includes(trimmedQuery))
    : options;

  const close = () => {
    setIsOpen(false);
    setQuery("");
  };

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={`dropdown ${isOpen ? "dropdown-open" : ""} ${className}`}
    >
      <button
        type="button"
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        className="btn btn-sm btn-ghost normal-case gap-2 border border-base-300"
      >
        {icon}
        <span>{displayLabel}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {isOpen && (
        <div className="dropdown-content z-[1] p-2 shadow-xl bg-base-100 rounded-lg w-52 mt-1 border border-base-300 flex flex-col">
          {showSearch && (
            <div className="relative mb-1 shrink-0">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="input input-sm w-full pl-7"
              />
            </div>
          )}
          <ul className="menu w-full flex-col flex-nowrap p-0 max-h-[50vh] overflow-y-auto overflow-x-hidden">
            {visibleOptions.map((option) => (
              <li key={option.value} className="w-full">
                <a
                  onClick={() => {
                    onChange(option.value);
                    close();
                  }}
                  className={`text-sm flex gap-2 items-center ${
                    value === option.value ? "active" : ""
                  }`}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </a>
              </li>
            ))}
            {visibleOptions.length === 0 && (
              <li className="p-2 text-xs text-base-content/60">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
